/**
 * Flatten an API error body whose `error` is an object into a string.
 *
 * WHY. The pricing gate's refusals (server/lib/creditGate.js: 402
 * credits_exhausted / plan_required, 409 project_archived; routes/projects.js
 * restore 402) answer `{ error: { type, message }, code, ... }` — the
 * Anthropic-style envelope the gateway and SDK clients also speak, pinned by
 * server tests. About sixty client call sites do
 * `setError(err.response?.data?.error || 'fallback')` and render the result,
 * so an object reached React as a child and the whole page crashed (React
 * #31, "found: object with keys {type, message}"): seen on staging
 * 2026-10-08 when a task page asked to wake a just-archived app
 * (POST /dev-server/start → 409 project_archived).
 *
 * Applied once, in the shared axios instance's error interceptor
 * (services/api.ts), so every call site gets a string. Nothing is lost:
 * the type moves to `errorType`, and `code` (which the classifiers read:
 * isPlanRequired, the credits_exhausted checks, startFirstTask) is set from
 * it when the server left it out. An object without a string message
 * becomes null so each call site's own fallback wording shows.
 *
 * Mutates and returns `data`; anything that is not that shape is untouched.
 */
export function flattenApiErrorBody<T = any>(data: T): T {
  const d: any = data;
  if (!d || typeof d !== 'object') return data;
  const e = d.error;
  if (!e || typeof e !== 'object' || Array.isArray(e)) return data;
  const type = typeof e.type === 'string' ? e.type : null;
  if (type && d.errorType == null) d.errorType = type;
  if (type && d.code == null) d.code = type;
  d.error = typeof e.message === 'string' && e.message ? e.message : null;
  return data;
}
